import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@saas-kit/ui';

import { SendToCounterDialog } from './SendToCounterDialog';
import { TransferDialog } from './TransferDialog';
import type { QmsToken } from './types';
import { usePrintToken } from './usePrintToken';
import type { WorkstationState } from './useWorkstation';

interface Props {
  ws: WorkstationState;
  current: QmsToken | null;
}

/** The counter action grid — call/recall/serve/complete plus the
 *  exception actions (no-show, transfer, send-to-counter) and reprint. */
export function WorkstationActions({ ws, current }: Props): JSX.Element {
  const { t } = useTranslation('qms');
  const { print, isPrinting } = usePrintToken();
  const [transferOpen, setTransferOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const status = current?.status ?? null;
  const called = status === 'called' || status === 'recalled';
  const active = called || status === 'serving';
  const noCurrent = current === null;

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <Button
          disabled={!noCurrent || ws.next.isPending}
          onClick={() => ws.next.mutate()}
        >
          {t('workstation.actions.call_next')}
        </Button>
        <Button
          variant="secondary"
          disabled={!called || ws.recall.isPending}
          onClick={() => current && ws.recall.mutate(current.id)}
        >
          {t('workstation.actions.recall')}
        </Button>
        <Button
          disabled={!called || ws.serve.isPending}
          onClick={() => current && ws.serve.mutate(current.id)}
        >
          {t('workstation.actions.serve')}
        </Button>
        <Button
          variant="secondary"
          disabled={status !== 'serving' || ws.complete.isPending}
          onClick={() => current && ws.complete.mutate(current.id)}
        >
          {t('workstation.actions.complete')}
        </Button>
        <Button
          variant="destructive"
          disabled={!called || ws.noShow.isPending}
          onClick={() => current && ws.noShow.mutate(current.id)}
        >
          {t('workstation.actions.no_show')}
        </Button>
        <Button
          variant="secondary"
          disabled={!active}
          onClick={() => setTransferOpen(true)}
        >
          {t('workstation.actions.transfer')}
        </Button>
        <Button
          variant="secondary"
          disabled={!active}
          onClick={() => setSendOpen(true)}
        >
          {t('workstation.actions.send_to_counter')}
        </Button>
        <Button
          variant="secondary"
          disabled={noCurrent || isPrinting}
          onClick={() => current && void print(current.id)}
        >
          {t('workstation.actions.reprint')}
        </Button>
      </div>
      {current ? (
        <>
          <TransferDialog
            open={transferOpen}
            token={current}
            ws={ws}
            onClose={() => setTransferOpen(false)}
          />
          <SendToCounterDialog
            open={sendOpen}
            token={current}
            ws={ws}
            onClose={() => setSendOpen(false)}
          />
        </>
      ) : null}
    </div>
  );
}
